import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { useUpdateInteractionStatus } from 'hooks/interactionStatus';
import { hasManualNotes, type ManualNotes } from 'hooks/useManualNotes';
import { getApiErrorMessage } from 'utils/apiError';
import { trackError } from 'utils/trackError';

export const CANCEL_WITHOUT_CONFIRMATION_SECONDS = 15;

interface UseCancelRecordingOptions {
  elapsedTime: number;
  interactionId: string | undefined;
  notes: ManualNotes;
  hasImmunizations: boolean;
  pauseRecording: () => Promise<void>;
  abandonRecording: () => void;
}

export function useCancelRecording({
  elapsedTime,
  interactionId,
  notes,
  hasImmunizations,
  pauseRecording,
  abandonRecording,
}: UseCancelRecordingOptions) {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { mutateAsync: discardInteraction } = useUpdateInteractionStatus();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const cancelInFlightRef = useRef(false);

  const cancel = useCallback(async () => {
    if (cancelInFlightRef.current) return;
    cancelInFlightRef.current = true;
    setIsCancelling(true);
    try {
      if (interactionId) {
        try {
          await pauseRecording();
        } catch (error) {
          trackError(error, { context: 'cancelRecording', action: 'pause' });
          toast.error(getApiErrorMessage(error, t));
          return;
        }
        // The mutation reports its own failure; the recording stays paused and can be cancelled again.
        await discardInteraction({ interactionId, status: 'discarded' });
      }
      abandonRecording();
      trackEvent(ANALYTICS_EVENTS.PATIENT_DISCARD_RECORDING);
      setIsConfirming(false);
      setIsCancelled(true);
    } catch {
      return;
    } finally {
      cancelInFlightRef.current = false;
      setIsCancelling(false);
    }
  }, [interactionId, pauseRecording, discardInteraction, abandonRecording, trackEvent, t]);

  const requestCancel = () => {
    const needsConfirmation =
      elapsedTime >= CANCEL_WITHOUT_CONFIRMATION_SECONDS ||
      hasManualNotes(notes) ||
      hasImmunizations;
    if (needsConfirmation) setIsConfirming(true);
    else void cancel();
  };

  return {
    isConfirming,
    isCancelling,
    isCancelled,
    requestCancel,
    confirmCancel: () => void cancel(),
    dismissConfirmation: () => setIsConfirming(false),
  };
}
